import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown, ChevronRight, Folder, HardDrive } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const slug = (s) => s.replace(/[^a-z0-9]+/gi, "-");

export function FolderPickerDialog({ open, title, folders = [], initial = "", rootLabel, allowRoot = false, manual = true, onSubmit, onClose, isPending, testidPrefix = "folder-picker" }) {
  const { t } = useTranslation();
  const [selected, setSelected] = useState("");
  const [manualPath, setManualPath] = useState("");
  const [expanded, setExpanded] = useState(() => new Set());

  useEffect(() => {
    if (open) {
      setSelected(initial || "");
      setManualPath("");
      setExpanded(new Set());
    }
  }, [open, initial]);

  const sorted = useMemo(() => [...folders].sort(), [folders]);

  const nodes = useMemo(() => {
    const seen = new Set();
    const flat = [];
    for (const p of sorted) {
      const parts = p.split("/");
      for (let i = 1; i <= parts.length; i++) {
        const path = parts.slice(0, i).join("/");
        if (!seen.has(path)) {
          seen.add(path);
          flat.push({ path, depth: i - 1 });
        }
      }
    }
    const visible = flat.filter((n) => {
      if (n.depth === 0) return true;
      let cur = n.path;
      while (cur.includes("/")) {
        cur = cur.split("/").slice(0, -1).join("/");
        if (!expanded.has(cur)) return false;
      }
      return true;
    });
    return visible.map((n) => ({ ...n, hasChildren: sorted.some((x) => x !== n.path && x.startsWith(n.path + "/")) }));
  }, [sorted, expanded]);

  const toggleExpand = (path) =>
    setExpanded((prev) => {
      const n = new Set(prev);
      n.has(path) ? n.delete(path) : n.add(path);
      return n;
    });

  const target = manualPath.trim() || selected;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid={`${testidPrefix}-dialog`}>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <Label>{t("folderPicker.selectTarget")}</Label>
          <div className="max-h-64 overflow-y-auto rounded-md border p-1.5" data-testid={`${testidPrefix}-tree`}>
            {allowRoot && (
              <button
                type="button"
                onClick={() => { setSelected(""); setManualPath(""); }}
                className={`flex w-full items-center gap-2 rounded px-2 py-1.5 text-sm font-medium ${selected === "" && !manualPath ? "bg-sky-100 text-sky-800" : "hover:bg-slate-100"}`}
                data-testid={`${testidPrefix}-root`}
              >
                <HardDrive className="h-4 w-4 text-slate-500" /> {rootLabel || t("folderPicker.root")}
              </button>
            )}
            {nodes.length === 0 && <p className="px-2 py-3 text-center text-xs text-slate-400">{t("folderPicker.noFolders")}</p>}
            {nodes.map((n) => (
              <div key={n.path} className="flex items-center" style={{ paddingInlineStart: n.depth * 18 }}>
                {n.hasChildren ? (
                  <button
                    type="button"
                    onClick={() => toggleExpand(n.path)}
                    className="p-1 text-slate-400 hover:text-slate-700"
                    data-testid={`${testidPrefix}-expand-${slug(n.path)}`}
                  >
                    {expanded.has(n.path) ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5 rtl:-scale-x-100" />}
                  </button>
                ) : (
                  <span className="inline-block w-[22px]" />
                )}
                <button
                  type="button"
                  onClick={() => { setSelected(n.path); setManualPath(""); }}
                  className={`flex flex-1 items-center gap-2 rounded px-2 py-1.5 text-sm ${selected === n.path && !manualPath ? "bg-sky-100 font-medium text-sky-800" : "hover:bg-slate-100"}`}
                  data-testid={`${testidPrefix}-folder-${slug(n.path)}`}
                >
                  <Folder className="h-4 w-4 shrink-0 text-amber-500" />
                  <span className="truncate">{n.path.split("/").pop()}</span>
                </button>
              </div>
            ))}
          </div>
          {manual && (
            <div className="space-y-1.5">
              <Label className="text-xs text-slate-500">{t("folderPicker.typeManually")}</Label>
              <Input value={manualPath} onChange={(e) => setManualPath(e.target.value)} placeholder={t("common.folder")} data-testid={`${testidPrefix}-manual-input`} />
            </div>
          )}
        </div>
        <DialogFooter>
          <Button onClick={() => onSubmit(target)} disabled={isPending || (!allowRoot && !target)} data-testid={`${testidPrefix}-submit`}>
            {t("common.move")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
